import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subject } from 'rxjs';
import { QuestionnaireType } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { Patient, PatientService } from 'src/app/core/api/patient.service';
import { SideSheetService } from 'src/app/shared/side-sheet/side-sheet.service';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService,
  SendQuestionnaireDialogData
} from 'src/app/core/api/questionnaire-request.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import { LayoutsService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import {
  QuestionnaireResponseModalComponent,
  QuestionnaireResponseModalData
} from 'src/app/shared/consult-forms/questionnaire-response-modal/questionnaire-response-modal.component';
import { SendQuestionnaireDialogComponent } from 'src/app/shared/send-questionnaire-dialog/send-questionnaire-dialog.component';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';

@Component({
  selector: 'csi-linked-questionnaires',
  templateUrl: './linked-questionnaires.component.html',
  styleUrls: ['./linked-questionnaires.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkedQuestionnairesComponent implements OnInit, OnDestroy {
  @Input() assessment: Assessment;
  @Input() isLocked: boolean;
  recommendedQuestionnaireTypes: string[] = [];

  private _assessmentMethod: SymptomModes;
  @Input() set assessmentMethod(value: SymptomModes) {
    this._assessmentMethod = value;
    this.recommendedQuestionnaireTypes = this._computeRecommendedTypes();
  }
  get assessmentMethod(): SymptomModes {
    return this._assessmentMethod;
  }

  @Input() set onQuestionnaireLinked$(subject: Subject<void>) {
    if (subject) {
      subject.pipe(untilDestroyed(this)).subscribe(() => {
        this.changeRef.markForCheck();
      });
    }
  }

  @Output() openLinkModal = new EventEmitter<void>();

  public readonly displayedColumns = ['type', 'completedAt', 'status', 'score', 'actions'];

  private currentPatient: Patient = null;

  constructor(
    public patientService: PatientService,
    public questionnaireRequestService: QuestionnaireRequestService,
    public staffPermissionsService: StaffPermissionsService,
    private appStateService: AppStateService,
    private sideSheetService: SideSheetService,
    private changeRef: ChangeDetectorRef,
    public layoutsService: LayoutsService
  ) {}

  ngOnInit() {
    this.appStateService.currentPatient$.pipe(untilDestroyed(this)).subscribe(patient => {
      this.currentPatient = patient;
      this.changeRef.markForCheck();
    });
  }

  ngOnDestroy() {}

  get latestLinkedQuestionnaires(): QuestionnaireRequest[] {
    if (!this.assessment || this.assessment.id === 'new') return [];
    if (
      !this.currentPatient ||
      !this.currentPatient.questionnaireRequests ||
      !this.currentPatient.questionnaireRequests.items
    ) {
      return [];
    }
    const linked = (this.currentPatient.questionnaireRequests
      .items as QuestionnaireRequest[]).filter(q => {
      return q.assessment && q.assessment.id === this.assessment.id;
    });
    const byType = this.questionnaireRequestService.getLastCompletedLinkedQuestionnaireByType(
      linked
    );
    return Object.keys(byType).map(k => byType[k]);
  }

  private _computeRecommendedTypes(): string[] {
    if (!this._assessmentMethod) return [];
    const layout = this.layoutsService.allLayoutsById[this._assessmentMethod];
    const settings: any =
      layout && layout.symptomSettings
        ? layout.symptomSettings
        : this.layoutsService.defaultSymptomSettings;
    return Object.keys(settings).filter(
      key => Object.keys(QuestionnaireType).includes(key) && settings[key]
    );
  }

  showQuestionnaireResponse(questionnaire: QuestionnaireRequest) {
    this.sideSheetService.open<
      QuestionnaireResponseModalComponent,
      QuestionnaireResponseModalData,
      void
    >(
      QuestionnaireResponseModalComponent,
      QuestionnaireResponseModalComponent.buildDialogData(
        questionnaire,
        null,
        this.patientService.patient.id
      )
    );
  }

  openSendDialog() {
    if (!this.staffPermissionsService.permissions.canSendQuestionnaires) return;
    if (this.isLocked || !this.currentPatient) return;
    this.sideSheetService.open<
      SendQuestionnaireDialogComponent,
      SendQuestionnaireDialogData,
      QuestionnaireRequest | QuestionnaireRequest[]
    >(
      SendQuestionnaireDialogComponent,
      SendQuestionnaireDialogComponent.buildDialogData(null, this.currentPatient, null, true, true)
    );
  }
}
